'use client';

import { Canvas } from '@react-three/fiber';
import { useRef, Suspense } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { EffectComposer, Vignette } from '@react-three/postprocessing';
import PinkTube from '../PinkTube/PinkTube';

function InteractiveLight() {
  const lightRef = useRef<THREE.PointLight>(null);
  useFrame((state) => {
    if (lightRef.current) {
      // Smoothly follow the mouse pointer for glossy glint highlights
      const targetX = state.pointer.x * 6;
      const targetY = state.pointer.y * 6;
      lightRef.current.position.x += (targetX - lightRef.current.position.x) * 0.08;
      lightRef.current.position.y += (targetY - lightRef.current.position.y) * 0.08;
    }
  });
  return <pointLight ref={lightRef} intensity={18} color="#FFB6C1" distance={25} position={[0, 0, 8]} />;
}

function CameraController() {
  useFrame((state) => {
    // Subtle parallax effect on camera position based on mouse position
    const targetX = state.pointer.x * 0.5;
    const targetY = state.pointer.y * 0.5;
    state.camera.position.x += (targetX - state.camera.position.x) * 0.05;
    state.camera.position.y += (targetY - state.camera.position.y) * 0.05;
    state.camera.lookAt(0, 0, -10);
  });
  return null;
}

export default function SceneCanvas() {
  const tipRef = useRef<THREE.Mesh>(null);

  return (
    <div className="fixed inset-0 -z-10 w-full h-full bg-zinc-950">
      <Canvas
        camera={{ position: [0, 0, 7.5], fov: 62 }} // Wider lens & closer camera for deep perspective escorzo
        dpr={[1, 2]}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: "high-performance",
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.2
        }}
      >
        <color attach="background" args={['#09090b']} />
        
        <Suspense fallback={null}>
          {/* Cinematic Lighting Setup */}
          <ambientLight intensity={0.3} />
          
          {/* Strong rim light from back-right to catch the edges of the loops */}
          <directionalLight position={[8, 5, -12]} intensity={5.5} color="#FFF0F5" />
          
          {/* Strong key light from front-right to define three-dimensional volume */}
          <directionalLight position={[5, 4, 7]} intensity={3.5} color="#FFE4E1" />
          
          {/* Soft fill light from front-left */}
          <directionalLight position={[-5, 4, 8]} intensity={3.2} color="#FFD1DC" />
          
          {/* Warm amber rim light from the top-front to add color contrast */}
          <directionalLight position={[0, 8, 2]} intensity={1.5} color="#FFE4B5" />
          
          {/* Ambient warmth from the bottom */}
          <pointLight position={[0, -6, -5]} intensity={15} color="#D01760" distance={30} />
          
          {/* Interactive light that follows the mouse */}
          <InteractiveLight />

          {/* 3D Content */}
          <PinkTube tipRef={tipRef} />

          {/* Camera Control */}
          <CameraController />

          {/* Cinematic Post-Processing */}
          <EffectComposer>
            {/* Dark lens vignette to focus attention to the center */}
            <Vignette eskil={false} offset={0.5} darkness={0.8} />
          </EffectComposer>
        </Suspense>
      </Canvas>
    </div>
  );
}
